import {
  Autocomplete,
  FormField,
  Icon,
  IconButton,
  Input,
  Tooltip,
} from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { NoOptions } from './NoOptions';
import { type AnalyticalFieldValueMappingV2 } from '../../../bookkeep/settings/accounting';

type Props = {
  mappingValues: AnalyticalFieldValueMappingV2[];
  entity: { id: string; name: string };
  onValueSelected: (value: AnalyticalFieldValueMappingV2 | undefined) => void;
  onDelete?: () => void;
  selectedValue?: Option;
  hasError: boolean;
};

type Option = AnalyticalFieldValueMappingV2 & {
  key: string;
  label: string;
};

const mapOption = (value: AnalyticalFieldValueMappingV2): Option => ({
  ...value,
  key: value.id,
  label: value.externalName,
});

export const AnalyticalFieldValueOption = ({
  mappingValues,
  entity,
  hasError,
  onValueSelected,
  onDelete,
  selectedValue,
}: Props) => {
  const { t, activeLanguage } = useTranslation('global');
  const [options, setOptions] = useState<Option[]>([]);
  const isDeletable = !!onDelete;

  useEffect(() => {
    setOptions(
      mappingValues.map(mapOption).sort((a, b) => {
        return a.label.localeCompare(b.label, activeLanguage, {
          sensitivity: 'base',
        });
      }),
    );
  }, [mappingValues]);

  const handleSearch = (value: string | undefined) => {
    if (!value) {
      setOptions(mappingValues.map(mapOption));
      return;
    }

    setOptions((previous) =>
      previous.filter((option) =>
        option.label.toLowerCase().includes(value.toLowerCase()),
      ),
    );
  };

  const availableOptions = options.filter(({ internalId }) => !internalId);
  return (
    <div
      key={`analytical_field_mapping_${entity.id}`}
      className={`grid ${isDeletable ? 'grid-cols-[1fr_16px_1fr_40px]' : 'grid-cols-[1fr_16px_1fr]'} items-center gap-8 pt-8`}
    >
      <div>
        <Input
          className="w-full"
          type="string"
          value={entity.name}
          name={`spendesk-value-${entity.id}`}
          isDisabled
        />
      </div>
      <div className="flex h-full items-center justify-center">
        <Icon size="m" name="arrow-right" />
      </div>
      <div>
        <FormField
          label={t(
            'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.integrationLabel',
          )}
          visuallyHideLabel
        >
          <Autocomplete<Option>
            fit="parent"
            isInvalid={hasError}
            options={availableOptions}
            value={selectedValue}
            onSearch={handleSearch}
            showClearSelectionButton
            onSelect={(option) => {
              onValueSelected(option);
            }}
            placeholder={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.autoCompletePlaceholder',
            )}
            renderNoOptions={() => <NoOptions />}
          />
        </FormField>
      </div>
      {isDeletable && (
        <div className="flex items-center justify-center">
          <Tooltip
            content={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.deleteSpendeskValue',
            )}
            placement="top"
            triggerAsChild
          >
            <IconButton
              iconName="trash"
              variant="tertiaryNeutral"
              aria-label={entity.name ?? ''}
              hasNegativeMargins
              onClick={onDelete}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};
